import { useEffect, useRef } from 'react';
import MessageBus from '@restore/messagebus';
import SplitIOTreatment from '../components/splitio/splitioTreatment';
import splitsDictionary from '../services/utils/splitsDictionary';
import { getRealm } from './utils/getRealm';
import { AuthProvider } from '../context/AuthContext';
import KeycloakApp from './KeycloakApp';
import LegacyApp from './LegacyApp';
import { storeOfflineEvent, sendOfflineEvents } from './utils/connectivityTracker';
import { Platform } from './types';

const App: React.FC = () => {
  const realm = getRealm(window.location.host);
  const messageBus = new MessageBus();
  const platformRef = useRef<Platform | undefined>();

  useEffect(() => {
    window.addEventListener('offline', storeOfflineEvent);
    window.addEventListener('online', sendOfflineEvents);

    return () => {
      window.removeEventListener('offline', storeOfflineEvent);
      window.removeEventListener('online', sendOfflineEvents);
    };
  }, []);

  const checkValidPlatform = (platform: Platform): platform is Platform => {
    return (
      platform === 'Care' ||
      platform === 'Home' ||
      platform === 'Insights' ||
      platform === 'Therapy' ||
      platform === 'Wellness'
    );
  };

  messageBus.subscribe('getPlatform', (payload) => {
    if (payload && typeof payload === 'object' && 'requestID' in payload) {
      messageBus.respond('getPlatform', payload.requestID as number, platformRef.current);
    }
  });

  messageBus.subscribe('setPlatform', (platform) => {
    if (checkValidPlatform(platform as Platform)) {
      platformRef.current = platform as Platform;
      messageBus.publish('platformChanged', platform);
    } else {
      console.error(`Error: Invalid platform "${platform}".`);
    }
  });

  messageBus.subscribe('getRealm', (payload) => {
    const realm = getRealm(window.location.host);
    if (payload && typeof payload === 'object' && 'requestID' in payload) {
      messageBus.respond('getRealm', payload.requestID as number, realm);
    }
  });

  return (
    <SplitIOTreatment
      featureName={splitsDictionary.KEYCLOAK_AUTH.name}
      treatmentValue="on"
      trafficType={splitsDictionary.KEYCLOAK_AUTH.traffic_type}
    >
      {(treatment: string) => {
        if (treatment === 'on' && realm !== false) {
          return (
            <AuthProvider>
              <KeycloakApp realm={realm} />
            </AuthProvider>
          );
        } else if (treatment === 'off' || !realm) {
          return <LegacyApp />;
        } else {
          return <>Loading...</>;
        }
      }}
    </SplitIOTreatment>
  );
};

export default App;
